// Financial Report — Finance → Financial Report tab.
//
// This is a REPORT BUILDER, not an uploader. The user's mental model should be
// "I am creating this month's Financial Report", with importing the Xero export
// as step one of that job rather than the job itself. Concretely that means:
//
//   · a persistent progress rail — Import → Process → Review → Export — so the
//     reporting process is always visible, not implied
//   · the report's structure is shown BEFORE anything is imported, so the
//     deliverable is what the user sees first
//   · the processing stage names what Atlas is actually doing to the numbers
//   · Review presents the finished report, with mapping questions alongside it
//     rather than as a gate in front of it
//
// REPORT-TYPE AGNOSTIC: no user-facing string names a specific Xero report.
// James's sample export and template haven't arrived, so the language stays
// generic ("financial export", "financial data") and whatever title the file
// carries is displayed verbatim. Don't reintroduce "P&L"/"Profit and Loss"
// labels here — see readPreamble() in xeroParse.js.
//
// STATELESS BY DESIGN — no database, no network. Everything lives in component
// state until the user exports. Saved report history is a deliberate follow-up
// (it needs a table + an authorised-write whitelist entry), and this is shaped
// so it slots in behind buildReport() later.

import { useCallback, useMemo, useState } from 'react';
import { Button } from '../../ui/button';
import { IconDownload, IconCheck } from '../../ui/icons';
import { useToast } from '../../lib/Toast';
import { parseXeroWorkbook, XeroParseError } from './xeroParse';
import { buildReport, EXCLUDE, fmtMoney, fmtPct } from './reportModel';
import { REPORT_LINES } from './accountMap';
import { downloadEomReport } from './exportEomXlsx';

const SERIF = '\'Tiempos\', Georgia, \'Times New Roman\', serif';
const MONO = '\'JetBrains Mono\', ui-monospace, \'SF Mono\', Menlo, monospace';

const JADE = '#036E43';
const INK = '#0F1419';
const MUTED = '#5C6166';
const FAINT = '#8B9094';
const LINE = '#EFEDE7';
const PAPER = '#FAFAF8';
const RED = '#B91C1C';

const ACCEPT = '.xlsx,.xlsm,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

const ASSIGNABLE = REPORT_LINES.filter(l => l.kind === 'input');

// The four stages of producing a report. Rendered as a persistent rail so the
// user can always see where they are in the process.
const STAGES = [
  { key: 'import', label: 'Import', hint: 'Xero financial export' },
  { key: 'process', label: 'Process', hint: 'Map & calculate' },
  { key: 'review', label: 'Review', hint: 'Check the report' },
  { key: 'export', label: 'Export', hint: 'Excel for management' },
];

// What Atlas does to the numbers. Surfaced live during processing, then kept as
// a summary on the review screen so the transformation stays visible.
const STEP_DEFS = [
  { key: 'validate', label: 'Validating the workbook' },
  { key: 'read', label: 'Reading financial data' },
  { key: 'map', label: 'Mapping accounts to report categories' },
  { key: 'calculate', label: 'Calculating report figures' },
  { key: 'crosscheck', label: 'Cross-checking totals against Xero' },
];

export default function EomReport() {
  const toast = useToast();
  const [phase, setPhase] = useState('import');   // import | processing | review
  const [error, setError] = useState(null);
  const [parsed, setParsed] = useState(null);
  const [fileName, setFileName] = useState('');
  const [periodKey, setPeriodKey] = useState('');
  const [overrides, setOverrides] = useState({});
  const [steps, setSteps] = useState({});         // stepKey → 'done' | 'active' | 'failed'
  const [exported, setExported] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [dragging, setDragging] = useState(false);

  const report = useMemo(() => {
    if (!parsed || !periodKey) return null;
    try { return buildReport(parsed, periodKey, overrides); }
    catch { return null; }
  }, [parsed, periodKey, overrides]);

  const onFile = useCallback(async (file) => {
    if (!file) return;
    setError(null);
    setFileName(file.name);
    setPhase('processing');
    setExported(false);

    // Stages tick from REAL milestones — there are no artificial delays here.
    // A small file flies through; a large workbook genuinely dwells on
    // "Reading financial data". The yield below is one paint frame so React
    // can show each state, not padding to make the work look harder.
    const mark = async (key, status) => {
      setSteps(s => ({ ...s, [key]: status }));
      await new Promise(r => requestAnimationFrame(() => r()));
    };

    try {
      setSteps({});
      await mark('validate', 'active');
      const buf = await file.arrayBuffer();

      await mark('validate', 'done');
      await mark('read', 'active');
      const p = await parseXeroWorkbook(buf);
      const first = p.periods[0]?.key || '';
      await mark('read', 'done');

      await mark('map', 'active');
      const initial = buildReport(p, first, {});
      await mark('map', 'done');

      await mark('calculate', 'active');
      await mark('calculate', 'done');

      await mark('crosscheck', 'active');
      await mark('crosscheck', initial.reconciliation.status === 'variance' ? 'failed' : 'done');

      setParsed(p);
      setPeriodKey(first);
      setOverrides({});
      setPhase('review');
    } catch (err) {
      const isParse = err instanceof XeroParseError;
      setSteps(s => {
        // Mark whichever stage was mid-flight as failed.
        const next = { ...s };
        const active = Object.keys(next).find(k => next[k] === 'active');
        if (active) next[active] = 'failed';
        return next;
      });
      setError({
        code: isParse ? err.code : 'UNKNOWN',
        message: isParse ? err.message : `Couldn't read that file: ${err?.message || 'unexpected error'}`,
      });
      setPhase('import');
      setParsed(null);
    }
  }, []);

  const reset = () => {
    setPhase('import'); setParsed(null); setError(null); setFileName('');
    setPeriodKey(''); setOverrides({}); setSteps({}); setExported(false);
  };

  const setOverride = (rowIndex, value) => {
    setOverrides(o => {
      const next = { ...o };
      if (!value) delete next[rowIndex]; else next[rowIndex] = value;
      return next;
    });
  };

  const doExport = async () => {
    if (!report) return;
    setDownloading(true);
    try {
      const name = await downloadEomReport(report);
      setExported(true);
      toast.success('Financial Report exported', name);
    } catch (err) {
      toast.error('Could not build the Excel file', err?.message || 'Unexpected error');
    } finally {
      setDownloading(false);
    }
  };

  const currentStage = phase === 'import' ? 'import'
    : phase === 'processing' ? 'process'
    : exported ? 'export' : 'review';

  return (
    <div>
      <PageHeading report={report} fileName={fileName} onReset={phase !== 'import' ? reset : null} />
      <ProgressRail current={currentStage} exported={exported} />

      {phase === 'import' && (
        <ImportStage
          error={error}
          steps={steps}
          dragging={dragging}
          setDragging={setDragging}
          onFile={onFile}
        />
      )}

      {phase === 'processing' && <ProcessingStage steps={steps} fileName={fileName} />}

      {phase === 'review' && report && (
        <ReviewStage
          report={report}
          parsed={parsed}
          periodKey={periodKey}
          setPeriodKey={setPeriodKey}
          overrides={overrides}
          setOverride={setOverride}
          steps={steps}
          onExport={doExport}
          downloading={downloading}
          exported={exported}
        />
      )}
    </div>
  );
}

// ── heading ────────────────────────────────────────────────────────────

function PageHeading({ report, fileName, onReset }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
      gap: 20, flexWrap: 'wrap', marginBottom: 20,
    }}>
      <div>
        <h2 style={{
          fontFamily: SERIF, fontSize: 24, fontWeight: 400, color: INK,
          margin: 0, letterSpacing: '-0.015em',
        }}>
          {report ? (report.meta?.orgName || 'Financial Report') : 'Create Financial Report'}
        </h2>
        <p style={{ fontSize: 12.5, color: MUTED, margin: '4px 0 0', lineHeight: 1.5 }}>
          {report
            ? <>{report.meta?.periodLabel || '—'}{fileName && <> · <span style={{ fontFamily: MONO, fontSize: 10.5 }}>{fileName}</span></>}</>
            : 'Atlas turns your Xero financial export into the standard management report.'}
        </p>
      </div>
      {onReset && <Button kind="ghost" onClick={onReset}>Start a new report</Button>}
    </div>
  );
}

// ── progress rail ──────────────────────────────────────────────────────

function ProgressRail({ current, exported }) {
  const idx = STAGES.findIndex(s => s.key === current);
  return (
    <div style={{
      display: 'flex', alignItems: 'stretch', gap: 0,
      border: `1px solid ${LINE}`, borderRadius: 12, overflow: 'hidden',
      marginBottom: 24, background: '#fff',
    }}>
      {STAGES.map((s, i) => {
        const done = i < idx || (s.key === 'export' && exported);
        const active = i === idx;
        return (
          <div
            key={s.key}
            style={{
              flex: 1, padding: '11px 16px',
              borderLeft: i === 0 ? 'none' : `1px solid ${LINE}`,
              background: active ? '#ECF6F0' : done ? '#FCFDFC' : '#fff',
              display: 'flex', alignItems: 'center', gap: 10, minWidth: 0,
            }}
          >
            <span style={{
              width: 20, height: 20, borderRadius: '50%', flexShrink: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontFamily: MONO, fontSize: 10, fontWeight: 600,
              background: done ? JADE : active ? JADE : '#F1EFE9',
              color: done || active ? '#fff' : FAINT,
            }}>
              {done ? '✓' : i + 1}
            </span>
            <span style={{ minWidth: 0 }}>
              <span style={{
                display: 'block', fontSize: 12.5, fontWeight: active ? 600 : 500,
                color: active ? JADE : done ? INK : FAINT,
              }}>{s.label}</span>
              <span style={{
                display: 'block', fontSize: 10.5, color: FAINT,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>{s.hint}</span>
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ── stage 1: import ────────────────────────────────────────────────────
//
// Leads with WHAT WILL BE PRODUCED, not with a file picker. The outline is
// derived from REPORT_LINES so it can never drift from the real report.

function ImportStage({ error, dragging, setDragging, onFile }) {
  const sections = REPORT_LINES.filter(l => l.kind === 'header');

  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.15fr) minmax(0, 1fr)', gap: 24, alignItems: 'start' }}>
      {/* left — the deliverable */}
      <div style={{ border: `1px solid ${LINE}`, borderRadius: 12, overflow: 'hidden', background: '#fff' }}>
        <div style={{ padding: '14px 18px', borderBottom: `1px solid ${LINE}`, background: PAPER }}>
          <div style={{ fontFamily: SERIF, fontSize: 15, color: INK }}>The report Atlas will produce</div>
          <div style={{ fontSize: 11.5, color: MUTED, marginTop: 3 }}>
            Same structure every month, calculated from your Xero figures.
          </div>
        </div>
        <div style={{ padding: '6px 0' }}>
          {REPORT_LINES.map(l => {
            if (l.kind === 'header') {
              return (
                <div key={l.key} style={{
                  padding: '9px 18px 5px', fontSize: 9.5, fontWeight: 600,
                  letterSpacing: '0.08em', textTransform: 'uppercase', color: FAINT,
                }}>{l.label}</div>
              );
            }
            return (
              <div key={l.key} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '5px 18px 5px 28px', fontSize: 12.5,
                color: l.emphasis ? INK : MUTED,
                fontWeight: l.emphasis ? 600 : 400,
              }}>
                <span>{l.label}</span>
                <span style={{ fontFamily: MONO, fontSize: 10, color: '#D5D3CD' }}>
                  {l.kind === 'derived' ? 'calculated' : 'mapped'}
                </span>
              </div>
            );
          })}
        </div>
        <div style={{ padding: '11px 18px', borderTop: `1px solid ${LINE}`, fontSize: 11, color: FAINT, lineHeight: 1.6 }}>
          {sections.length} sections · {REPORT_LINES.filter(l => l.kind === 'derived').length} calculated
          figures · cross-checked against your Xero totals
        </div>
      </div>

      {/* right — step 1 of producing it */}
      <div>
        <div style={{ fontSize: 10.5, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: FAINT, marginBottom: 8 }}>
          Step 1 — Import your figures
        </div>
        <label
          onDragOver={e => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={e => { e.preventDefault(); setDragging(false); onFile(e.dataTransfer?.files?.[0]); }}
          style={{
            display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
            gap: 9, padding: '34px 22px', textAlign: 'center', cursor: 'pointer',
            border: `2px dashed ${dragging ? JADE : '#D5D3CD'}`, borderRadius: 12,
            background: dragging ? '#F2F9F5' : PAPER,
            transition: 'background 120ms, border-color 120ms',
          }}
        >
          <div style={{ color: '#C9C6BE' }}><IconSpreadsheet /></div>
          <div style={{ fontSize: 13.5, fontWeight: 600, color: INK }}>
            Drop your Xero financial export here
          </div>
          <div style={{ fontSize: 11.5, color: MUTED, maxWidth: 300, lineHeight: 1.55 }}>
            Or click to choose a file. Excel <code>.xlsx</code> straight from Xero —
            no need to tidy it up first.
          </div>
          <input
            type="file" accept={ACCEPT} hidden
            onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; onFile(f); }}
          />
        </label>

        {error && (
          <div style={{
            marginTop: 12, background: '#FEF2F2', border: '1px solid #FECACA',
            color: RED, borderRadius: 10, padding: '11px 13px', fontSize: 12.5, lineHeight: 1.55,
          }}>
            {error.message}
          </div>
        )}

        <div style={{
          marginTop: 16, padding: '13px 15px', background: '#FCFCFA',
          border: `1px solid ${LINE}`, borderRadius: 10,
        }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: INK, marginBottom: 5 }}>
            Getting the file out of Xero
          </div>
          <div style={{ fontSize: 11.5, color: MUTED, lineHeight: 1.6 }}>
            In Xero go to <b>Accounting → Reports</b>, open the financial report for the
            month, then <b>Export → Excel</b>. Comparative periods are fine — you'll
            choose which column to report on next.
          </div>
        </div>
      </div>
    </div>
  );
}

// ── stage 2: processing ────────────────────────────────────────────────

function ProcessingStage({ steps, fileName }) {
  return (
    <div style={{ maxWidth: 520, margin: '4px auto 0' }}>
      <div style={{ border: `1px solid ${LINE}`, borderRadius: 12, background: '#fff', padding: '20px 22px' }}>
        <div style={{ fontFamily: SERIF, fontSize: 15, color: INK, marginBottom: 3 }}>
          Building your Financial Report
        </div>
        <div style={{ fontSize: 11.5, color: FAINT, fontFamily: MONO, marginBottom: 16 }}>{fileName}</div>
        {STEP_DEFS.map(s => <StepRow key={s.key} label={s.label} status={steps[s.key]} />)}
      </div>
    </div>
  );
}

function StepRow({ label, status }) {
  const done = status === 'done';
  const active = status === 'active';
  const failed = status === 'failed';
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 0' }}>
      <span style={{
        width: 16, height: 16, borderRadius: '50%', flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 9, fontWeight: 700, color: '#fff',
        background: done ? JADE : failed ? RED : active ? '#C9C6BE' : '#F1EFE9',
      }}>{done ? '✓' : failed ? '!' : ''}</span>
      <span style={{
        fontSize: 12.5,
        color: done || failed ? INK : active ? MUTED : FAINT,
        fontWeight: active ? 600 : 400,
      }}>{label}</span>
    </div>
  );
}

// ── stage 3: review (the report is the hero) ───────────────────────────

function ReviewStage({
  report, parsed, periodKey, setPeriodKey,
  overrides, setOverride, steps, onExport, downloading, exported,
}) {
  const m = report.metrics;
  const { unmapped, lowConfidence, doubleCounted } = report.review;
  const questions = [
    ...unmapped.map(u => ({ ...u, assignedTo: '', why: 'No rule matched this account' })),
    ...lowConfidence.map(c => ({ ...c, why: `Fell through to ${labelOf(c.assignedTo)} — please confirm` })),
  ];

  const accountsRead = parsed.rows.filter(r => r.kind === 'account').length;
  const mappedCount = Object.values(report.contributions).reduce((s, a) => s + a.length, 0);

  return (
    <div>
      {/* action bar */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: 14, flexWrap: 'wrap', marginBottom: 16,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          {(parsed.periods || []).length > 1 && (
            <label style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 12, color: MUTED }}>
              Reporting period
              <select
                value={periodKey}
                onChange={e => setPeriodKey(e.target.value)}
                style={{
                  fontFamily: 'inherit', fontSize: 12, padding: '6px 9px',
                  border: `1px solid ${LINE}`, borderRadius: 7, background: '#fff', color: INK,
                }}
              >
                {parsed.periods.map(p => <option key={p.key} value={p.key}>{p.key}</option>)}
              </select>
            </label>
          )}
          <ProcessSummary
            accountsRead={accountsRead}
            mappedCount={mappedCount}
            questions={questions.length}
            steps={steps}
          />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {exported && (
            <span style={{ fontSize: 11.5, color: JADE, display: 'flex', alignItems: 'center', gap: 5 }}>
              <IconCheck /> Exported
            </span>
          )}
          <Button kind="primary" icon={<IconDownload />} onClick={onExport}>
            {downloading ? 'Building…' : exported ? 'Export again' : 'Export report (Excel)'}
          </Button>
        </div>
      </div>

      <Reconciliation rec={report.reconciliation} />

      {/* questions, if any — alongside the report, not blocking it */}
      {(questions.length > 0 || doubleCounted.length > 0) && (
        <MappingQuestions
          questions={questions}
          doubleCounted={doubleCounted}
          overrides={overrides}
          setOverride={setOverride}
        />
      )}

      {/* headline figures */}
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(178px, 1fr))',
        gap: 12, marginBottom: 22,
      }}>
        <Stat label="Net Sales" value={fmtMoney(m.net_sales)} sub="After discounts" />
        <Stat label="Gross Profit" value={fmtMoney(m.gross_profit)} sub="Net sales less COGS" />
        <Stat label="Sales Margin" value={fmtPct(m.sales_margin)} sub="Gross profit ÷ net sales" accent={JADE} />
        <Stat label="Advertising" value={fmtMoney(m.advertising)} sub={adShare(m)} />
        <Stat
          label="Net Profit" value={fmtMoney(m.net_profit)}
          sub={`${fmtPct(m.net_margin)} of net sales`}
          accent={m.net_profit != null && m.net_profit < 0 ? RED : JADE}
        />
      </div>

      {/* the report */}
      <div style={{ border: `1px solid ${LINE}`, borderRadius: 12, overflow: 'hidden', background: '#fff' }}>
        <div style={{
          padding: '12px 16px', borderBottom: `1px solid ${LINE}`, background: PAPER,
          display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12,
        }}>
          <span style={{ fontFamily: SERIF, fontSize: 15, color: INK }}>Financial Report</span>
          <span style={{ fontFamily: MONO, fontSize: 10.5, color: FAINT, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
            {report.periodKey}
          </span>
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr>
              <Th>Line</Th>
              <Th align="right">{report.periodKey}</Th>
              <Th>Contributing Xero accounts</Th>
            </tr>
          </thead>
          <tbody>
            {report.lines.map(line => {
              if (line.kind === 'header') {
                return (
                  <tr key={line.key} style={{ background: '#F7F6F2', borderTop: `1px solid ${LINE}` }}>
                    <td colSpan={3} style={{
                      padding: '7px 16px', fontSize: 10, fontWeight: 600,
                      letterSpacing: '0.07em', textTransform: 'uppercase', color: FAINT,
                    }}>{line.label}</td>
                  </tr>
                );
              }
              const isPct = line.numFmt === 'pct';
              return (
                <tr key={line.key} style={{
                  borderTop: `1px solid ${LINE}`,
                  background: line.emphasis ? '#FCFDFC' : 'transparent',
                }}>
                  <td style={{
                    padding: '9px 16px', paddingLeft: line.emphasis ? 16 : 28,
                    fontWeight: line.emphasis ? 600 : 400, color: INK,
                  }}>{line.label}</td>
                  <td style={{
                    padding: '9px 16px', textAlign: 'right',
                    fontFamily: MONO, fontSize: 12.5,
                    fontWeight: line.emphasis ? 600 : 400,
                    fontVariantNumeric: 'tabular-nums',
                    color: line.value != null && line.value < 0 ? RED : INK,
                  }}>{isPct ? fmtPct(line.value) : fmtMoney(line.value)}</td>
                  <td style={{ padding: '9px 16px', fontSize: 11.5, color: FAINT }}>
                    {(line.contributors || []).length ? summarise(line.contributors) : ''}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div style={{ fontSize: 11, color: FAINT, marginTop: 12, lineHeight: 1.6, maxWidth: 660 }}>
        The exported workbook contains this report plus an <b>Account Mapping</b> sheet
        listing every Xero account and the line it fed — so any figure can be traced
        back without re-opening Xero.
      </div>
    </div>
  );
}

// Compact, durable evidence of the transformation.
function ProcessSummary({ accountsRead, mappedCount, questions, steps }) {
  const checked = steps.crosscheck === 'done';
  return (
    <div style={{ fontSize: 11.5, color: FAINT, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
      <span>{accountsRead} accounts read</span>
      <Dot />
      <span>{mappedCount} mapped</span>
      {questions > 0 && <><Dot /><span style={{ color: '#B45309' }}>{questions} to confirm</span></>}
      {checked && <><Dot /><span style={{ color: JADE }}>totals cross-checked</span></>}
    </div>
  );
}
function Dot() { return <span style={{ color: '#D5D3CD' }}>·</span>; }

function MappingQuestions({ questions, doubleCounted, overrides, setOverride }) {
  return (
    <div style={{
      border: `1px solid #F0E4C8`, borderRadius: 12, overflow: 'hidden',
      marginBottom: 20, background: '#FFFDF7',
    }}>
      <div style={{ padding: '12px 16px', borderBottom: '1px solid #F5EBD6' }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: '#8A5A00' }}>
          {questions.length > 0
            ? `${questions.length} account${questions.length === 1 ? '' : 's'} to confirm`
            : 'Mapping issue'}
        </div>
        <div style={{ fontSize: 11.5, color: '#9A6B12', marginTop: 2, lineHeight: 1.5 }}>
          Placed by a catch-all rule rather than an explicit one. The report below updates
          as you reassign. Nothing is dropped silently.
        </div>
      </div>

      {doubleCounted.length > 0 && (
        <div style={{ padding: '10px 16px', background: '#FEF2F2', color: RED, fontSize: 12 }}>
          {doubleCounted.length} account{doubleCounted.length === 1 ? '' : 's'} counted in two
          lines: {doubleCounted.map(c => c.label).join(', ')}. This is a rule bug — worth flagging.
        </div>
      )}

      {questions.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5, background: '#fff' }}>
          <thead>
            <tr style={{ background: PAPER }}>
              <Th>Xero account</Th>
              <Th>Section</Th>
              <Th align="right">Amount</Th>
              <Th>Report line</Th>
              <Th>Why it's here</Th>
            </tr>
          </thead>
          <tbody>
            {questions.map(r => (
              <tr key={r.rowIndex} style={{ borderTop: `1px solid ${LINE}` }}>
                <Td>{r.label}</Td>
                <Td muted>{r.section || '—'}</Td>
                <Td align="right" mono>{fmtMoney(r.value, { decimals: 2 })}</Td>
                <Td>
                  <select
                    value={overrides[r.rowIndex] ?? r.assignedTo ?? ''}
                    onChange={e => setOverride(r.rowIndex, e.target.value)}
                    style={{
                      fontFamily: 'inherit', fontSize: 12, padding: '5px 8px',
                      border: `1px solid ${overrides[r.rowIndex] ? JADE : LINE}`,
                      borderRadius: 6, background: '#fff', color: INK, minWidth: 170,
                    }}
                  >
                    <option value="">— not mapped —</option>
                    {ASSIGNABLE.map(l => <option key={l.key} value={l.key}>{l.label}</option>)}
                    <option value={EXCLUDE}>Exclude from report</option>
                  </select>
                </Td>
                <Td muted>{r.why}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

function Reconciliation({ rec }) {
  const tone = rec.status === 'ok'
    ? { bg: '#ECF6F0', border: '#A7D8C0', fg: JADE, dot: JADE, label: 'Totals tie to Xero' }
    : rec.status === 'variance'
      ? { bg: '#FEF2F2', border: '#FECACA', fg: RED, dot: '#DC2626', label: 'Variance against Xero' }
      : { bg: PAPER, border: LINE, fg: MUTED, dot: FAINT, label: 'Not cross-checked' };

  return (
    <div style={{
      display: 'flex', alignItems: 'flex-start', gap: 10,
      background: tone.bg, border: `1px solid ${tone.border}`,
      borderRadius: 10, padding: '11px 14px', marginBottom: 18,
    }}>
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: tone.dot, marginTop: 5, flexShrink: 0 }} />
      <div style={{ fontSize: 12.5, color: tone.fg, lineHeight: 1.5 }}>
        <b>{tone.label}.</b> {rec.message}
        {rec.status === 'variance' && (
          <span style={{ fontFamily: MONO, fontSize: 11, display: 'block', marginTop: 3, opacity: 0.85 }}>
            Xero {fmtMoney(rec.xero, { decimals: 2 })} · this report {fmtMoney(rec.computed, { decimals: 2 })}
          </span>
        )}
      </div>
    </div>
  );
}

// ── helpers ────────────────────────────────────────────────────────────

function labelOf(key) { return REPORT_LINES.find(l => l.key === key)?.label || key; }

// Spreadsheet glyph for the import zone. ui/icons.jsx's IconFile renders a
// text badge ("FILE"), which reads as clutter at this size — this matches the
// app's thin-stroke line-icon language instead.
function IconSpreadsheet() {
  return (
    <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M4 3h11l5 5v13a0 0 0 0 1 0 0H4z" />
      <path d="M14 3v6h6" />
      <path d="M7 13h10M7 17h10M11.5 12v7" />
    </svg>
  );
}

function adShare(m) {
  if (m.advertising == null || !m.net_sales) return 'Marketing spend';
  return `${((m.advertising / m.net_sales) * 100).toFixed(1)}% of net sales`;
}

function summarise(contributors) {
  const names = contributors.map(c => c.label);
  if (names.length <= 2) return names.join(', ');
  return `${names.slice(0, 2).join(', ')} +${names.length - 2} more`;
}

function Stat({ label, value, sub, accent }) {
  return (
    <div style={{ background: PAPER, border: `1px solid ${LINE}`, borderRadius: 12, padding: '14px 18px' }}>
      <div style={{ fontSize: 10, fontWeight: 500, color: FAINT, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: accent || INK, marginTop: 3, fontVariantNumeric: 'tabular-nums' }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: FAINT, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function Th({ children, align }) {
  return (
    <th style={{
      padding: '9px 16px', textAlign: align || 'left',
      fontSize: 10, fontWeight: 600, letterSpacing: '0.06em',
      textTransform: 'uppercase', color: FAINT,
    }}>{children}</th>
  );
}

function Td({ children, align, muted, mono }) {
  return (
    <td style={{
      padding: '9px 16px', textAlign: align || 'left',
      color: muted ? FAINT : INK,
      fontFamily: mono ? MONO : 'inherit',
      fontSize: mono ? 12 : 'inherit',
      fontVariantNumeric: mono ? 'tabular-nums' : 'normal',
    }}>{children}</td>
  );
}
